/**
 * SUNTRONIX ONE — Insights section stub + real pages.
 *
 * Phase 2 (Lifecycle Sankey) → Executive Pulse hosts it.
 * Phase 3 (Ops Control Tower + SLA Heatmap + Needs Attention) → live below.
 * Phase 4 (Deployment funnel · Recovery + first-pickup · Courier scorecard) → live below.
 * Phase 5-7 → stubs with real Metric-Engine drill targets prepped.
 */
import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { StubPage, GlobalFilterBar, MetricCard } from "./_shared";
import { RefreshCw, AlertTriangle, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";

// ---- Small helpers ---------------------------------------------------------

function useInsights(path) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  const load = useCallback(async () => {
    setBusy(true);
    try { const { data } = await api.get(path); setData(data); }
    catch { setData({ error: true }); }
    finally { setBusy(false); }
  }, [path]);
  useEffect(() => { load(); }, [load, path]);
  return { data, busy, reload: load };
}

function PageShell({ title, subtitle, children, reload, testId }) {
  return (
    <div className="space-y-4 max-w-[1400px]" data-testid={testId}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500">Insights</div>
          <h1 className="text-2xl font-heading font-semibold text-slate-900 tracking-tight">{title}</h1>
          <p className="text-[13px] text-slate-500 mt-1">{subtitle}</p>
        </div>
        {reload && (
          <Button size="sm" variant="outline" onClick={reload}>
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
          </Button>
        )}
      </div>
      {children}
    </div>
  );
}

function FreshnessLine({ ts }) {
  return (
    <div className="text-[11px] text-slate-400">
      {ts ? `Data through ${new Date(ts).toLocaleString()}` : "Loading…"}
    </div>
  );
}

// ============================================================================
// PHASE 3 — OPERATIONS CONTROL TOWER
// ============================================================================
export function Operations() {
  const { data, reload } = useInsights("/insights/control-tower");
  const { data: needs } = useInsights("/insights/needs-attention");
  return (
    <PageShell title="Operations Control Tower"
      subtitle="Live queues, needs-attention list, exceptions."
      reload={reload} testId="insights-operations">
      <GlobalFilterBar value={{ preset: "This Month" }} onChange={() => {}} />
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
        {(data?.cards || []).map((c) => (
          <Link key={c.id} to={c.drill_to || "#"}
            className="bg-white border border-slate-200 rounded-md p-4 hover:border-slate-400 transition-colors"
            data-testid={`ct-card-${c.id}`}>
            <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">{c.label}</div>
            <div className="mt-2 text-2xl font-semibold text-slate-900">{(c.total ?? 0).toLocaleString()}</div>
            {c.need_action ? (
              <div className="mt-1 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 inline-block">
                {c.need_action} need action
              </div>
            ) : null}
          </Link>
        ))}
      </div>
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="px-4 py-2.5 border-b border-slate-200 flex items-center gap-2">
          <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />
          <div className="text-[13px] font-medium text-slate-900">Needs attention now</div>
          <div className="ml-auto text-[11px] text-slate-500">{needs?.count || 0} categories</div>
        </div>
        {(needs?.items || []).length === 0 ? (
          <div className="px-4 py-8 text-center text-slate-400 text-[13px]">Nothing needs attention right now.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
              <tr>
                <th className="text-left px-3 py-2">Category</th>
                <th className="text-left px-3 py-2">Count</th>
                <th className="text-left px-3 py-2">Severity</th>
                <th className="text-left px-3 py-2">Oldest</th>
                <th className="text-left px-3 py-2">Owner</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {needs.items.map((r) => (
                <tr key={r.category} data-testid={`na-row-${r.category}`}>
                  <td className="px-3 py-2 text-[13px] text-slate-900">{r.category}</td>
                  <td className="px-3 py-2 text-[13px]">{r.count}</td>
                  <td className="px-3 py-2">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] ${
                      r.severity === "high" ? "bg-rose-100 text-rose-800" : "bg-amber-100 text-amber-800"}`}>
                      {r.severity}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-[12px] text-slate-600">
                    {r.oldest_age_days != null ? `${r.oldest_age_days}d` : "—"}
                  </td>
                  <td className="px-3 py-2 text-[12px] text-slate-600 capitalize">{r.owner || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <FreshnessLine ts={data?.updated_at} />
    </PageShell>
  );
}

// ============================================================================
// PHASE 3 — SLA HEATMAP
// ============================================================================
export function SlaAnalytics() {
  const { data, reload } = useInsights("/insights/sla-heatmap");
  const rows = data?.rows || [];
  const states = data?.states || [];
  const heatColor = (v) => v == null ? "#F8FAFC"
    : v === 0 ? "#F8FAFC"
    : v < 10 ? "#FEF3C7"
    : v < 50 ? "#FDBA74"
    : "#FCA5A5";
  return (
    <PageShell title="SLA & Service Quality"
      subtitle="Services × states heatmap. Cells with real signal light up; unmodelled cells stay '—'."
      reload={reload} testId="insights-sla">
      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-[10px] uppercase text-slate-500">
              <th className="text-left px-3 py-2">Service</th>
              {states.map((s) => <th key={s} className="text-center px-3 py-2 whitespace-nowrap">{s}</th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((row) => (
              <tr key={row.service} data-testid={`sla-row-${row.service}`}>
                <td className="px-3 py-2 text-[13px] font-medium text-slate-900">{row.service}</td>
                {states.map((s) => {
                  const v = row.cells[s];
                  return (
                    <td key={s} className="px-3 py-1.5">
                      <div className="w-full h-9 rounded flex items-center justify-center text-[12px] font-medium"
                        style={{ backgroundColor: heatColor(v), color: v ? "#0F172A" : "#94A3B8" }}
                        data-testid={`sla-cell-${row.service}-${s}`}>
                        {v ?? "—"}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <FreshnessLine ts={data?.updated_at} />
    </PageShell>
  );
}

// ============================================================================
// PHASE 4 — DEPLOYMENT FUNNEL
// ============================================================================
export function Deployment() {
  const { data, reload } = useInsights("/insights/deployment-funnel");
  return (
    <PageShell title="Deployment Analytics"
      subtitle="Requested → Approved → Dispatched → Completed."
      reload={reload} testId="insights-deployment">
      <Funnel stages={data?.stages} testIdPrefix="dep-stage" insufficient={data?.insufficient_data} />
      <FreshnessLine ts={data?.updated_at} />
    </PageShell>
  );
}

// ============================================================================
// PHASE 4 — RECOVERY + FIRST-PICKUP
// ============================================================================
export function Recovery() {
  const { data, reload } = useInsights("/insights/recovery-flow");
  return (
    <PageShell title="Recovery Analytics"
      subtitle="Requested → Approved → In progress → Completed. First-pickup success measured across completed pickups."
      reload={reload} testId="insights-recovery">
      <div className="bg-white border border-slate-200 rounded-md p-4">
        <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 flex items-center gap-1">
          <TrendingUp className="h-3 w-3" /> First pickup success
        </div>
        <div className="mt-1 text-3xl font-semibold text-slate-900" data-testid="first-pickup-value">
          {data?.first_pickup_insufficient_data ? "—" : `${data?.first_pickup_success_pct ?? 0}%`}
        </div>
        {data?.first_pickup_insufficient_data && (
          <div className="text-[11px] text-slate-400 mt-1">Insufficient completed pickups yet.</div>
        )}
      </div>
      <Funnel stages={data?.stages} testIdPrefix="rec-stage" />
      <FreshnessLine ts={data?.updated_at} />
    </PageShell>
  );
}

// ============================================================================
// PHASE 4 — COURIER SCORECARD
// ============================================================================
export function Courier() {
  const { data, reload } = useInsights("/insights/courier-scorecard");
  return (
    <PageShell title="Courier & Logistics"
      subtitle="Scorecard by courier. On-time % excludes not-yet-delivered shipments."
      reload={reload} testId="insights-courier">
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        {data?.insufficient_data ? (
          <div className="px-4 py-8 text-center text-slate-400 text-[13px]">No shipment data available yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
              <tr>
                <th className="text-left px-3 py-2">Courier</th>
                <th className="text-left px-3 py-2">Shipments</th>
                <th className="text-left px-3 py-2">Delivered</th>
                <th className="text-left px-3 py-2">Failed</th>
                <th className="text-left px-3 py-2">On-time %</th>
                <th className="text-right px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(data?.rows || []).map((r) => (
                <tr key={r.courier} data-testid={`courier-row-${r.courier}`}>
                  <td className="px-3 py-2 text-[13px] font-medium text-slate-900">{r.courier}</td>
                  <td className="px-3 py-2 text-[13px]">{r.shipments}</td>
                  <td className="px-3 py-2 text-[13px] text-emerald-700">{r.delivered}</td>
                  <td className="px-3 py-2 text-[13px] text-rose-700">{r.failed}</td>
                  <td className="px-3 py-2 text-[13px]">{r.on_time_pct ?? "—"}%</td>
                  <td className="px-3 py-2 text-right">
                    <Link to={r.drill_to} className="text-[11px] underline text-slate-600">Open</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <FreshnessLine ts={data?.updated_at} />
    </PageShell>
  );
}

// ---- Funnel component ------------------------------------------------------
function Funnel({ stages, testIdPrefix, insufficient }) {
  if (insufficient) {
    return <div className="border border-dashed border-slate-300 rounded-md p-8 text-center text-slate-500 text-[13px]">Insufficient data yet.</div>;
  }
  const maxV = Math.max(1, ...(stages || []).map((s) => s.value || 0));
  return (
    <div className="bg-white border border-slate-200 rounded-md p-4 space-y-2">
      {(stages || []).map((s, i) => {
        const prev = i > 0 ? stages[i - 1] : null;
        const conv = prev && prev.value ? ((s.value / prev.value) * 100).toFixed(1) : null;
        const width = `${Math.max(4, (s.value / maxV) * 100)}%`;
        return (
          <Link key={s.id} to={s.drill_to || "#"} className="block group"
            data-testid={`${testIdPrefix}-${s.id}`}>
            <div className="flex items-center gap-3">
              <div className="w-32 text-[12px] text-slate-700">{s.label}</div>
              <div className="flex-1 relative h-8 rounded bg-slate-50 overflow-hidden">
                <div className="absolute inset-y-0 left-0 bg-slate-800 group-hover:bg-slate-900 transition-colors flex items-center px-3"
                     style={{ width }}>
                  <span className="text-[12px] font-medium text-white">{(s.value || 0).toLocaleString()}</span>
                </div>
              </div>
              <div className="w-16 text-right text-[11px] text-slate-500">
                {conv !== null ? `${conv}%` : ""}
              </div>
            </div>
          </Link>
        );
      })}
    </div>
  );
}

// ============================================================================
// PHASE 5 — Technology / Inventory / Reuse (real metric endpoints, minimal UI)
// ============================================================================
export function Technology() {
  const { data } = useInsights("/insights/asset-ageing");
  return (
    <PageShell title="Technology Estate"
      subtitle="Asset ageing distribution — each bucket drills to the filtered asset list."
      testId="insights-technology">
      <div className="bg-white border border-slate-200 rounded-md p-4 space-y-2">
        {(data?.rows || []).map((r) => (
          <Link key={r.bucket} to={r.drill_to || "#"} className="block group" data-testid={`ageing-${r.bucket}`}>
            <div className="flex items-center gap-3">
              <div className="w-16 text-[12px] text-slate-700">{r.bucket}</div>
              <div className="flex-1 relative h-8 rounded bg-slate-50 overflow-hidden">
                <div className="absolute inset-y-0 left-0 bg-slate-800"
                  style={{ width: `${Math.max(2, Math.min(100, r.value * 3))}%` }} />
              </div>
              <div className="w-16 text-right text-[12px] text-slate-700">{r.value}</div>
            </div>
          </Link>
        ))}
      </div>
      <FreshnessLine ts={data?.updated_at} />
    </PageShell>
  );
}

export const Inventory = () => <StubPage testId="insights-inventory" phase={5}
  title="Inventory Flow"
  description="Opening → Purchase / Recovered / Customer → Available → Deployed / Reserved / Repair / Reuse / Storage / ITAD → Closing."
  plannedItems={["Inventory Sankey with real quantities","Ageing by source",
    "Accuracy · Shortage · Excess · Reserved · In-transit"]} />;

export function Reuse() {
  const { data } = useInsights("/insights/reuse-before-buy");
  return (
    <PageShell title="Reuse Before Buy"
      subtitle="Available reuse inventory and reservations. New-demand matching lands with the Technology Exchange."
      testId="insights-reuse">
      <Funnel stages={data?.stages} testIdPrefix="reuse-stage" />
      <FreshnessLine ts={data?.updated_at} />
    </PageShell>
  );
}

export const Support = () => <StubPage testId="insights-support" phase={4}
  title="Support Analytics" description="Tickets · TAT · Failure categories · Warranty coverage."
  plannedItems={["Rate-based failure analytics","Warranty expiry buckets","No-fault-found + parts required"]} />;

// ============================================================================
// PHASE 6 — COMMERCIAL / BILLING / DQ
// ============================================================================
export function Commercial() {
  const { data } = useInsights("/insights/revenue-leakage");
  return (
    <PageShell title="Commercial"
      subtitle="Revenue leakage — completed but not billed, missing PO, missing rate."
      testId="insights-commercial">
      <MetricCard metric={{
        id: "revenue_leakage", value: data?.total_value,
        unit: "INR", insufficient_data: data?.insufficient_data,
        definition: "Total value stuck in billing exceptions."
      }} testId="commercial-leakage-card" />
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
            <tr><th className="text-left px-3 py-2">Category</th><th className="text-left px-3 py-2">Count</th><th className="text-left px-3 py-2">Value</th><th></th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(data?.rows || []).map((r) => (
              <tr key={r.id} data-testid={`leakage-row-${r.id}`}>
                <td className="px-3 py-2 text-[13px]">{r.label}</td>
                <td className="px-3 py-2 text-[13px]">{r.count}</td>
                <td className="px-3 py-2 text-[13px]">₹{Math.round(r.value || 0).toLocaleString()}</td>
                <td className="px-3 py-2 text-right"><Link to={r.drill_to || "#"} className="text-[11px] underline">Open</Link></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <FreshnessLine ts={data?.updated_at} />
    </PageShell>
  );
}

export const Billing = () => <StubPage testId="insights-billing" phase={6}
  title="Billing" description="Completed → Billable → Rate/PO → Certified → Invoiced → Paid."
  plannedItems={["Billing funnel with conversion %","Reconciliation buckets","Cash collection ageing"]} />;

export function DataQuality() {
  const { data } = useInsights("/insights/data-quality");
  return (
    <PageShell title="Data Quality"
      subtitle="Golden Asset Record quality by dimension. No single aggregate hides deficiencies."
      testId="insights-dq">
      {data?.insufficient_data ? (
        <div className="border border-dashed border-slate-300 rounded-md p-8 text-center text-slate-500 text-[13px]">
          No asset data available to score yet.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {(data?.dimensions || []).map((d) => (
              <Link key={d.id} to={d.drill_to || "#"}
                className="bg-white border border-slate-200 rounded-md p-4 hover:border-slate-400"
                data-testid={`dq-${d.id}`}>
                <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">{d.label}</div>
                <div className="mt-1 text-2xl font-semibold text-slate-900">{d.score}%</div>
                <div className="mt-2 h-1.5 rounded bg-slate-100 overflow-hidden">
                  <div className="h-full bg-emerald-500" style={{ width: `${d.score}%` }} />
                </div>
              </Link>
            ))}
          </div>
          <div className="text-[11px] text-slate-500">
            Aggregate: <b>{data?.aggregate}%</b> — never used to hide dimension-level issues.
          </div>
        </>
      )}
      <FreshnessLine ts={data?.updated_at} />
    </PageShell>
  );
}

// ============================================================================
// PHASE 7 — CUSTOMER HEALTH / CATALOGUE / REPORTS (stubs)
// ============================================================================
export const CustomerHealth = () => <StubPage testId="insights-customer-health" phase={7}
  title="Customer Operational Health"
  description="Transparent health model — SLA, exceptions, ageing orders, failed pickups, inventory accuracy, DQ, billing exceptions."
  plannedItems={["Status: Healthy / Watch / Attention / Critical / Insufficient data (with driver breakdown)","Never an unexplained AI score"]} />;

export const Catalogue = () => <StubPage testId="insights-catalogue" phase={7}
  title="Catalogue Analytics" description="Views · searches · zero-result · shortlists · conversions."
  plannedItems={["Zero-result searches with Add Product / Map Alias actions","Website-to-order funnel"]} />;

export const Reports = () => <StubPage testId="insights-reports" phase={7}
  title="Reports & Scheduling" description="Saved views · scheduled reports · alerts · exports."
  plannedItems={["Daily / Weekly / Monthly / Quarterly","Threshold alerts","PDF/XLSX/CSV exports honouring filters"]} />;
